import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { makeStyles } from '@material-ui/core';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import MissionTransportService from '../../../services/MissionTransportService';

moment.locale('fr');
const localizer = momentLocalizer(moment);

const EVENT_COLORS = ['#1976d2', '#7b1fa2', '#00897b', '#ef6c00', '#5d4037', '#455a64'];

const useStyles = makeStyles((theme) => ({
  calendarPaper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    overflow: 'hidden',
    border: '1px solid #dce6f3',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.07)',
  },
  calendarShell: {
    '& .rbc-calendar': {
      color: '#334155',
      fontFamily: 'Roboto, Arial, sans-serif',
    },
    '& .rbc-toolbar': {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1),
      borderRadius: '10px',
      backgroundColor: '#f4f7fb',
    },
    '& .rbc-toolbar-label': {
      color: '#29476f',
      fontSize: '1.05rem',
      fontWeight: 700,
      textTransform: 'capitalize',
    },
    '& .rbc-btn-group': {
      display: 'flex',
      gap: '4px',
    },
    '& .rbc-btn-group button': {
      padding: '7px 12px',
      border: '1px solid #cbd8e8',
      borderRadius: '7px !important',
      backgroundColor: '#ffffff',
      color: '#526581',
      fontWeight: 600,
      transition: 'all 160ms ease',
    },
    '& .rbc-btn-group button:hover, & .rbc-btn-group button:focus': {
      borderColor: '#1976d2',
      backgroundColor: '#eaf2ff',
      color: '#1976d2',
    },
    '& .rbc-btn-group button.rbc-active': {
      borderColor: '#1976d2',
      backgroundColor: '#1976d2',
      color: '#ffffff',
      boxShadow: '0 3px 8px rgba(25, 118, 210, 0.22)',
    },
    '& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view table': {
      overflow: 'hidden',
      border: '1px solid #dce6f3',
      borderRadius: '10px',
    },
    '& .rbc-header': {
      padding: '10px 6px',
      borderColor: '#dce6f3',
      backgroundColor: '#f8fafc',
      color: '#526581',
      fontSize: '0.78rem',
      fontWeight: 700,
      textTransform: 'uppercase',
    },
    '& .rbc-day-bg, & .rbc-month-row, & .rbc-time-content, & .rbc-timeslot-group': {
      borderColor: '#edf1f7',
    },
    '& .rbc-today': {
      backgroundColor: '#eef6ff',
    },
    '& .rbc-off-range-bg': {
      backgroundColor: '#f8fafc',
    },
    '& .rbc-date-cell': {
      padding: '6px 8px',
      color: '#526581',
      fontWeight: 600,
    },
    '& .rbc-event': {
      padding: '3px 6px',
      border: 0,
      borderRadius: '6px',
      boxShadow: '0 2px 5px rgba(15, 23, 42, 0.18)',
      fontSize: '0.78rem',
      fontWeight: 600,
    },
    '& .rbc-show-more': {
      color: '#1976d2',
      fontWeight: 700,
    },
    [theme.breakpoints.down('sm')]: {
      '& .rbc-toolbar': {
        alignItems: 'stretch',
        flexDirection: 'column',
      },
      '& .rbc-toolbar-label': {
        order: -1,
        padding: theme.spacing(0.75),
        textAlign: 'center',
      },
      '& .rbc-btn-group': {
        justifyContent: 'center',
        flexWrap: 'wrap',
      },
    },
  },
}));

const CalendarVehicle = () => {
  const classes = useStyles();
  const [events, setEvents] = useState([]);
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let active = true;

    const fetchEvents = async () => {
      setOpenLoadingSimpleBackdrop(true);
      setErrorMessage('');
      try {
        const response = await MissionTransportService.getVehicleCalendar();
        if (!active) return;

        if (!response?.status) {
          setEvents([]);
          if (response?.message) setErrorMessage(response.message);
          return;
        }

        const normalizedEvents = (Array.isArray(response.data) ? response.data : []).map((event) => ({
          ...event,
          start: new Date(event.start),
          end: moment(event.end).add(1, 'day').toDate(),
          inclusiveEnd: event.end,
          allDay: true,
        }));
        setEvents(normalizedEvents);
      } catch (error) {
        console.error('Erreur lors du chargement du calendrier des voitures :', error);
        if (active) {
          setEvents([]);
          setErrorMessage('Impossible de charger le calendrier des voitures.');
        }
      } finally {
        if (active) setOpenLoadingSimpleBackdrop(false);
      }
    };

    fetchEvents();
    return () => {
      active = false;
    };
  }, []);

  const handleSelectEvent = async (event) => {
    setOpenLoadingSimpleBackdrop(true);
    setErrorMessage('');
    try {
      const response = await MissionTransportService.detailEventCalendar(event.vhc_id, event.ids);
      if (!response?.status) {
        setErrorMessage(response?.message || "Impossible de récupérer le détail de l'affectation.");
        return;
      }

      setSelectedEvent({ data: response.data, event });
      setOpenDialog(true);
    } catch (error) {
      console.error("Erreur lors du chargement du détail de l'affectation :", error);
      setErrorMessage("Impossible de récupérer le détail de l'affectation.");
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEvent(null);
  };

  const eventPropGetter = (event) => {
    const color = EVENT_COLORS[Math.abs(Number(event.vhc_id) || 0) % EVENT_COLORS.length];
    return { style: { backgroundColor: color, borderColor: color } };
  };

  return (
    <>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />
      <Paper className={classes.calendarPaper} variant="outlined">
        {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
        {!events.length && !openLoadingSimpleBackdrop && !errorMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>Aucune affectation de voiture à afficher.</Alert>
        )}

        <Box className={classes.calendarShell}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            views={['month', 'week', 'day', 'agenda']}
            popup
            showMultiDayTimes
            eventPropGetter={eventPropGetter}
            style={{ height: 'clamp(620px, 75vh, 900px)' }}
            onSelectEvent={handleSelectEvent}
            messages={{
              date: 'Date',
              time: 'Heure',
              event: 'Événement',
              allDay: 'Toute la journée',
              week: 'Semaine',
              work_week: 'Semaine de travail',
              day: 'Jour',
              month: 'Mois',
              previous: 'Précédent',
              next: 'Suivant',
              yesterday: 'Hier',
              tomorrow: 'Demain',
              today: "Aujourd'hui",
              agenda: 'Agenda',
              noEventsInRange: "Il n'y a pas d'événement dans cette période.",
              showMore: (total) => `+ ${total} événement(s) supplémentaire(s)`,
            }}
          />
        </Box>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: '#ffffff' }}>
          Affectation de {selectedEvent?.data?.vhc_imm || 'la voiture'}
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Du</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {selectedEvent?.event?.start && moment(selectedEvent.event.start).format('DD/MM/YYYY')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Au</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {selectedEvent?.event?.inclusiveEnd && moment(selectedEvent.event.inclusiveEnd).format('DD/MM/YYYY')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Destination</Typography>
              <Typography>{selectedEvent?.event?.destination || '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Chauffeur</Typography>
              <Typography>{selectedEvent?.data?.driver_name || selectedEvent?.data?.name || '—'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Missionnaires</Typography>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedEvent?.data?.missionaries?.length ? selectedEvent.data.missionaries.map((missionary) => (
                  <Chip
                    key={missionary.user_id || missionary.missionary_timesheet_mobility_id}
                    label={missionary.name || missionary.nom}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )) : <Typography color="text.secondary">—</Typography>}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="primary" variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CalendarVehicle;
